import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import type { FirmwareInstallProgress } from '../../../redux/RobotDataSlice';
import { getElectronicExplorerElectronicI18n, getRemoteDeviceTypeFallbackDisplayName } from '../../../utils/remoteDeviceTypeDisplay';
import HelpPopover from '../../HelpPopover';

interface AccessoryUpdateStatusPanelProps {
  progressByDevice: FirmwareInstallProgress[];
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

function compareDeviceIndex(a: number, b: number): number {
  return a - b;
}

function deviceTypeGroupKey(deviceType: string | null): string {
  return deviceType ?? '__null__';
}

type GroupedAccessoryProgress = {
  groupKey: string;
  deviceType: string | null;
  /** Max clamped percent across all devices of this type in the current update batch. */
  maxAggregatedPercent: number;
  /** Minimum device index in the group — used only for stable row ordering. */
  sortOrder: number;
};

const AccessoryUpdateStatusPanel = ({ progressByDevice }: AccessoryUpdateStatusPanelProps) => {
  const { t, i18n: translationI18n } = useTranslation();
  const locale = translationI18n?.resolvedLanguage || translationI18n?.language || i18n.resolvedLanguage || i18n.language;
  const lastDisplayedPercentByTypeRef = useRef<Record<string, number>>({});

  const percentFormatter = useMemo(() => {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }, [locale]);

  const groupedByDeviceType = useMemo(() => {
    const map = new Map<string, GroupedAccessoryProgress>();
    for (const { deviceIndex, deviceType, progress } of progressByDevice) {
      const groupKey = deviceTypeGroupKey(deviceType);
      const pct = clampPercent(Number(progress));
      const existing = map.get(groupKey);
      if (!existing) {
        map.set(groupKey, {
          groupKey,
          deviceType,
          maxAggregatedPercent: pct,
          sortOrder: deviceIndex,
        });
      } else {
        existing.maxAggregatedPercent = Math.max(existing.maxAggregatedPercent, pct);
        existing.sortOrder = Math.min(existing.sortOrder, deviceIndex);
      }
    }
    return [...map.values()].sort((a, b) => compareDeviceIndex(a.sortOrder, b.sortOrder));
  }, [progressByDevice]);

  const activeGroupKeys = new Set(groupedByDeviceType.map((g) => g.groupKey));
  for (const key of Object.keys(lastDisplayedPercentByTypeRef.current)) {
    if (!activeGroupKeys.has(key)) {
      delete lastDisplayedPercentByTypeRef.current[key];
    }
  }

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" pb={1}>
        <Typography>
          <Trans i18nKey="connectedDrawer.accessory-firmware-updating">Updating accessory firmware</Trans>
        </Typography>
        <HelpPopover
          id="accessory-firmware-updating-popover"
          ariaLabel={t('aria-label.more-info', 'more information')}
          text={<Trans i18nKey="connectedDrawer.accessory-firmware-updating-info">Accessory firmware update in progress</Trans>}
        />
      </Stack>
      <Stack spacing={1.5} sx={{ width: '100%', mt: 1 }}>
        {groupedByDeviceType.map(({ groupKey, deviceType, maxAggregatedPercent }) => {
          const prevDisplayed = lastDisplayedPercentByTypeRef.current[groupKey] ?? 0;
          const pct = Math.max(prevDisplayed, maxAggregatedPercent);
          lastDisplayedPercentByTypeRef.current[groupKey] = pct;
          const unknownLabel = t('connectedDrawer.accessory-update.unknown-device-type', 'Unknown');

          const remoteDeviceTypeDisplayName =
            deviceType === null || deviceType.trim() === ''
              ? unknownLabel
              : (getElectronicExplorerElectronicI18n(deviceType, t) ?? getRemoteDeviceTypeFallbackDisplayName(deviceType)) || unknownLabel;

          return (
            <Box key={groupKey} sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('connectedDrawer.accessory-update.device', {
                    defaultValue: '{{deviceTypeName}}',
                    deviceTypeName: remoteDeviceTypeDisplayName,
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {`${percentFormatter.format(pct)}%`}
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={pct} sx={{ height: 4, borderRadius: 2 }} />
            </Box>
          );
        })}
      </Stack>
    </>
  );
};

export default AccessoryUpdateStatusPanel;
